<template>
  <div class="hero-section" :class="[{ lms: $config.project === 'lms', 'in-track': inTrack }, $config.platform]">
    <div class="container">
      <div class="row">
        <div class="col col-5 text">
          <span v-if="badgeText" class="badge" :class="`badge-${badgeText.toLowerCase()}`">
            {{ badgeText }}
          </span>
          <h1 :class="{ 'h3': $config.project === 'site', 'h2': $config.project === 'lms' }">
            {{ track.name }} {{ $config.project === 'site' ? 'Career Track' : '' }}
          </h1>
          <h2 v-if="track.subtitle" class="h4 subtitle">
            {{ track.subtitle }}
          </h2>
          <p class="body-1">
            {{ track.description }}
          </p>
          <template v-if="$store.getters['auth/user'].role === 'business-admin' && $route.name === 'business-library-slug'">
            <div class="info" :class="{ landing }">
              <div>
                <MonitorIcon /> {{ track.totalCourses }} Career Track Courses
              </div>
              <div>
                <ClockIcon /> {{ track.hoursOfVideo }} Hours of Video
              </div>
              <div>
                <MedalIcon /> {{ certificateText }}
              </div>
            </div>
            <nuxt-link :to="`/business/tasks/new/?track=${track.id}`" class="btn-primary btn-lg">
              Assign
            </nuxt-link>
          </template>
          <template v-else-if="$config.project === 'lms' && (track.enrolled || track.progress > 0)">
            <Progress
              v-if="track.enrolled || track.progress > 0 || track.certificateNumber"
              :progress="track.progress"
              :certificate="track.certificateNumber"
              :data="{ completed: +track.completedCourses, total: track.totalCourses }"
              text="courses"
              offset
              dark
              class="info"
            />
            <MedalIcon v-if="track.certificateNumber" class="completed" />

            <template v-if="track.finalExam && !track.certificateNumber">
              <template v-if="track.enrolled">
                <nuxt-link :to="`/exams/?tab=track&trackId=${track.id}`" class="final-exam-button btn-primary btn-lg">
                  Final exam
                </nuxt-link>

                <button v-if="track.attempted === 0" class="unenroll-button btn-primary-light btn-lg" @click="$emit('unenroll')">
                  Unenroll
                </button>
              </template>

              <button v-else class="enroll-button btn-primary btn-lg" @click="$emit('enroll')">
                Enroll Now
              </button>
            </template>

            <template v-else>
              <p v-if="hasLastWachedLenght && track.attempted < 2 && track.progress !== 100" class="last-watched" :class="{ completed: track.certificateNumber }">
                <small>Last watched: {{ track.lastWatched.name }}</small>
              </p>

              <template v-if="track.enrolled">
                <template v-if="track.certificateNumber">
                  <nuxt-link :to="`/certificates/${track.certificateNumber}/`" class="track-certificate-button btn-primary btn-lg">
                    Career track certificate
                  </nuxt-link>

                  <nuxt-link v-if="track.progress !== 100 && hasLastWachedLenght" class="watch-button btn-primary-transparent" :to="`/courses/${track.lastWatched.slug}/`">
                    Continue watching
                  </nuxt-link>

                  <nuxt-link v-else-if="track.progress !== 100" class="watch-button btn-primary-transparent" :to="`/courses/${track.courses[0].slug}/`">
                    Start watching
                  </nuxt-link>
                </template>

                <template v-else>
                  <div class="actions">
                    <nuxt-link v-if="hasLastWachedLenght" :to="`/courses/${track.lastWatched.slug}/`" class="continue-watch-button btn-primary btn-lg">
                      Continue watching
                    </nuxt-link>

                    <nuxt-link v-else :to="`/courses/${track.courses[0].slug}/`" class="continue-watch-button btn-primary btn-lg">
                      Start watching
                    </nuxt-link>

                    <button class="unenroll-button btn-primary-light btn-lg" @click="$emit('unenroll')">
                      Unenroll
                    </button>
                  </div>
                </template>
              </template>
              <button v-else class="enroll-button btn-primary btn-lg" @click="$emit('enroll')">
                Enroll Now
              </button>
            </template>
          </template>
          <template v-else>
            <div class="info" :class="{ landing }">
              <template v-if="landing">
                <div>
                  <CareerTrackIcon /> Choose a career track
                </div>
                <div>
                  <LearnIcon /> Learn from the best professionals in the field
                </div>
                <div>
                  <MedalIcon /> Pass the final exam and become a certified professional
                </div>
              </template>
              <template v-else>
                <div>
                  <MonitorIcon /> {{ track.totalCourses }} Career Track Courses
                </div>
                <div>
                  <ClockIcon /> {{ track.hoursOfVideo }} Hours of Video
                </div>
                <div>
                  <MedalIcon /> {{ certificateText }}
                </div>
              </template>
            </div>
            <template v-if="!landing">
              <nuxt-link
                v-if="$config.project === 'site'" to="/signup/" class="btn-primary btn-lg"
                data-event="start_your_career_1.click"
              >
                {{ $c.text($config.platform === 'ds' ? 'button.start-your-career' : 'button.get-started') }}
              </nuxt-link>
              <button v-else-if="!track.enrolled" class="enroll-button btn-primary btn-lg" @click="$emit('enroll')">
                Enroll Now
              </button>
            </template>
          </template>
        </div>
        <div v-if="inTrack" class="image col col-7">
          <img :src="$config.platform === 'ds' ? require(`../assets/images/career-track-${track.id}${lmsInTrack ? '-wide' : ''}.png`) : require(`../assets/images/fa/fa-career-track-${track.id}.jpg`)" :alt="track.name">
        </div>
        <div v-else class="video col col-7">
          <slot name="player" />
        </div>
      </div>
    </div>
    <img
      v-if="imageBackground"
      class="career-tracks-landing"
      :src="image"
      :alt="imageUrl"
    >
  </div>
</template>

<script>
import Progress from '../Courses/Progress';
import MonitorIcon from '../assets/svg/courses/monitor.svg?vue-component';
import ClockIcon from '../assets/svg/courses/clock.svg?vue-component';
import MedalIcon from '../assets/svg/courses/medal.svg?vue-component';
import CareerTrackIcon from '../assets/svg/icon-career-track.svg?vue-component';
import LearnIcon from '../assets/svg/icon-learn.svg?vue-component';

export default {
  components: {
    Progress, MonitorIcon, ClockIcon, MedalIcon, CareerTrackIcon, LearnIcon,
  },
  props: {
    track: {
      type: Object,
      default: () => ({
        name: 'Career Tracks',
        subtitle: 'Prepare for the Job You Want',
        description: 'Build the specific skills for the job you want with a selection of courses created by leading experts to help you achieve your professional goals.',
      }),
      required: false,
    },
    certificateText: { type: String, default: 'Certificate of Achievement' },
    offset: Boolean,
    inTrack: Boolean,
    landing: Boolean,
    imageBackground: Boolean,
    imageUrl: { type: String, default: '' },
  },
  computed: {
    lmsInTrack() {
      return this.$config.project === 'lms' && this.inTrack && this.$store.state.layout === 'desktop';
    },
    badgeText() {
      let t = '';
      if (this.track.certificateNumber) {
        t = 'Completed';
      } else if (!this.track.certificateNumber && this.track.enrolled) {
        t = 'Enrolled';
      }

      return t;
    },
    hasLastWachedLenght() {
      return this.track.lastWatched && Object.keys(this.track.lastWatched).length;
    },
    image() {
      // eslint-disable-next-line import/no-dynamic-require
      return require(`../assets/images/fa/${this.imageUrl}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.hero-section {
  background-color: var(--main-dark);
  position: relative;
  color: var(--main-light);
  height: 650px;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(253.23deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0.05) 44.75%, rgba(0, 164, 178, 0) 94.77%);
    transform: matrix(-1, 0, 0, 1, 0, 0);
  }

  &.lms {
    height: 545px;

    .badge {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      width: 92px;
      height: 28px;
      font-weight: 500;
      border-radius: 4px;
      margin-bottom: var(--spacing-8);

      &.badge-completed {
        background-color: rgba(188, 255, 203, 0.2);
        color: var(--main-green);
      }

      &.badge-enrolled {
        background-color: rgba(rgb(86, 191, 199), 0.2);
        color: var(--accent);
      }
    }

    .row {
      padding-top: 0;
    }

    .text {
      padding-right: var(--spacing-55);
    }

    .h2 {
      font-weight: 600;
      font-size: 38px;
      line-height: 45px;
      margin-bottom: 0;
    }

    .h4 {
      color: var(--main-dark-light-4);
      font-weight: 500;
      margin-bottom: 28px;
      margin-top: var(--spacing-8);

      &.subtitle {
        color: var(--main-light-shade-3);
      }
    }

    .body-1 {
      font-size: 16px;
      margin-bottom: 28px;
    }

    .info {
      margin-bottom: var(--spacing-28);

      &.landing {
        font-size: 14px;
      }

      svg {
        path[fill] {
          fill: var(--main-light);
        }

        path[stroke] {
          stroke: var(--main-light);
        }

        rect {
          fill: var(--main-light);
        }
      }
    }
  }

  &.in-track:not(.lms) {
    .container {
      display: flex;
      align-items: center;

      .row {
        padding-top: var(--spacing-55);
        display: flex;
        align-items: center;
      }
    }
  }

  .h3 {
    font-size: 38px;
    line-height: 45px;
    margin: 0 0 var(--spacing-16);
  }

  .body-1,
  .last-watched {
    color: var(--main-dark-shade-4);
  }

  .body-1 {
    margin-bottom: var(--spacing-28);
  }

  .last-watched {
    margin-bottom: 5px;
  }

  .row {
    padding-top: var(--spacing-80);

    .continue-watch-button,
    .final-exam-button,
    .track-certificate-button,
    .enroll-button {
      width: 224px;
      height: 40px;
      padding: 11px 1.25em;
    }

    .continue-watch-button,
    .track-certificate-button,
    .final-exam-button {
      margin-top: 0;
      margin-right: var(--spacing-12);
    }

    .enroll-button {
      margin-top: 0;
    }

    .watch-button {
      height: 40px;
      padding: 11px 16px;
    }

    .unenroll-button {
      height: 40px;
      padding: 11px 1.25em;
      border-color: transparent;

      &:not(:disabled):hover {
        border-color: var(--accent);
        color: var(--accent);
      }

      &:not(:disabled):active {
        background-color: var(--accent);
        border-color: var(--accent);
        color: var(--main-light);
      }
    }

    .completed {
      display: none;
    }
  }

  .info {
    margin-top: var(--spacing-24);

    div {
      display: flex;
      align-items: center;
      line-height: 18px;
      font-weight: 500;

      + div {
        margin-top: var(--spacing-16);
      }
    }

    svg {
      width: 22px;
      height: 22px;
      margin-right: var(--spacing-12);
      flex: 0 0 auto;
    }
  }

  .btn-primary {
    margin-top: var(--spacing-48);
    width: 300px;
    font-weight: 600;
  }

  .image,
  .video {
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .image img {
    max-width: 100%;
    margin-left: auto;
    display: block;
  }

  &.fa {
    &:before {
      background: none;
    }
  }
}

@include bp($bp-laptop) {
  .hero-section {
    &.lms {
      .h2 {
        font-size: 32px;
      }

      .h4 {
        font-size: 24px;
        line-height: 30px;
      }
    }

    .btn-primary {
      width: 225px;
    }
  }
}

@include bp($bp-mobile) {
  .hero-section {
    height: auto;
    padding-bottom: var(--spacing-80);

    &.in-track:not(.lms) {
      padding-bottom: var(--spacing-70);

      .container {
        .row {
          padding-top: 100px;

          .text {
            margin-top: 0;
            flex: 0 0 40%;
          }

          .image {
            flex: 0 0 60%;
            order: 2;

            img {
              max-width: 100%;
            }
          }
        }
      }

      @media screen and (max-width: 900px) {
        .container {
          padding-top: var(--spacing-70);

          .row {
            flex-direction: column;
            row-gap: 36px;
            padding-top: var(--spacing-40);

            .text {
              order: 2;
            }

            .image {
              order: 1;
            }
          }
        }
      }
    }

    &.in-track.lms {
      .container {
        .row {
          .text {
            margin-top: 0;
            flex: 0 0 40%;
            order: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }

          .image {
            flex: 0 0 60%;
            order: 2;

            img {
              max-width: 100%;
            }
          }
        }
      }

      @media screen and (max-width: 900px) {
        .container {
          .row {
            padding-top: 0;

            .text {
              flex: 0 0 100%;
              order: 2;
              padding-top: var(--spacing-28);

              .body-1 {
                margin-top: var(--spacing-28);
              }

              .info {
                margin-top: 0;
              }
            }

            .image {
              flex: 0 0 100%;
              order: 1;
            }
          }
        }
      }
    }

    .text {
      margin-top: var(--spacing-36);
    }

    .image {
      order: -1;

      img {
        margin: 0 auto 0 0;
        max-width: 350px;
        width: 100%;
      }
    }

    .video {
      order: -1;
      padding: 0;
      height: 100%;
    }

    .btn-primary {
      width: 100%;
      max-width: 350px;
    }

    &.lms {
      height: auto;
      padding-bottom: var(--spacing-32);

      .text {
        margin-top: var(--spacing-32);
        padding-left: var(--spacing-24);
        padding-right: var(--spacing-24);

        .body-1 {
          color: var(--main-light-shade-3);
        }

        .info {
          div {
            + div {
              margin-top: var(--spacing-12);
            }
          }
        }
      }

      &.in-track {
        padding-top: var(--spacing-36);

        .image {
          padding-left: var(--spacing-24);
          padding-right: var(--spacing-24);
        }
      }

      .container {
        padding: 0;

        .row {
          margin: 0;
          height: auto;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .hero-section .container {
    .continue-watch-button,
    .final-exam-button,
    .track-certificate-button,
    .enroll-button,
    .unenroll-button,
    .watch-button {
      width: 100%;
      max-width: none;
    }

    .unenroll-button,
    .watch-button {
      margin-top: var(--spacing-12);
    }

    .completed {
      display: block;
    }
  }

  .hero-section {
    &.in-track {
      padding-bottom: var(--spacing-100);

      .container {
        .row {
          padding-top: var(--spacing-108);

          .text {
            .h3 {
              font-size: 32px;
            }

            .body-1 {
              color: rgba(255, 255, 255, 0.67);
            }

            ::v-deep .course-progress {
              .progress-bar {
                width: 68px;
              }
            }

            .last-watched {
              margin-top: 0;
            }

            .actions {
              .btn-primary {
                height: 52px;
                margin-top: 0;
              }
            }

            .btn-primary {
              margin-top: var(--spacing-28);
              max-width: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }
      }
    }

    &.lms {
      padding-bottom: var(--spacing-40);
    }
  }
}

.fa {
  @import '../assets/sass/fa/components/career-tracks-hero.scss';
}
</style>
